import React, { useEffect } from 'react';
import {
  CreditCardIcon,
  BuildingLibraryIcon,
  XMarkIcon,
  BoltIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

export default function PaymentMethodModal({ isOpen, onClose, onSelect }) {
  // Close on Escape key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden transform transition-all animate-scaleUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="relative px-6 pt-6 pb-4 border-b border-slate-100 bg-gradient-to-r from-blue-50/50 via-white to-sky-50/50">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-blue-600 mb-1">
            <ShieldCheckIcon className="w-4 h-4 text-blue-600" />
            <span>Secure Checkout</span>
          </div>
          <h2 className="text-xl font-bold text-slate-900">
            Choose Payment Method
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Select how you would like to complete your subscription purchase.
          </p>
        </div>

        {/* Payment Options Grid */}
        <div className="p-6 space-y-4">
          {/* Option 1: PayPal & Card (Instant) */}
          <button
            type="button"
            onClick={() => onSelect('paypal')}
            className="group w-full text-left p-5 rounded-2xl border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50/40 bg-white shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                  <CreditCardIcon className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-base font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                      PayPal / Debit or Credit Card
                    </span>
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                      <BoltIcon className="w-3 h-3 text-emerald-600" />
                      Instant Activation
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    Pay securely using PayPal, Visa, MasterCard, or American Express.
                  </p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-500" />
                      Instant automated delivery
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-500" />
                      No receipt upload required
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-blue-600 text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap self-center hidden sm:block">
                Continue →
              </div>
            </div>
          </button>

          {/* Option 2: Manual Bank & Crypto (Receipt Upload) */}
          <button
            type="button"
            onClick={() => onSelect('manual')}
            className="group w-full text-left p-5 rounded-2xl border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50/40 bg-white shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center flex-shrink-0 group-hover:bg-slate-800 group-hover:text-white transition-colors">
                  <BuildingLibraryIcon className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-base font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                      Bank Transfer & Crypto
                    </span>
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">
                      Receipt Upload
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    Pay via Revolut, Pakistani Banks, EasyPaisa, JazzCash, or Binance Pay.
                  </p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <CheckCircleIcon className="w-3.5 h-3.5 text-blue-500" />
                      Revolut (EUR/GBP)
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <CheckCircleIcon className="w-3.5 h-3.5 text-blue-500" />
                      Meezan & Alfalah (PKR)
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <CheckCircleIcon className="w-3.5 h-3.5 text-blue-500" />
                      Binance (USDT)
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-blue-600 text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap self-center hidden sm:block">
                Continue →
              </div>
            </div>
          </button>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            256-Bit SSL Encrypted & Protected
          </span>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
